import { apiClient } from './client';

/**
 * Client for `/api/alerts/*` (Phase 14 Day 2). Stays in lockstep with
 * the server's Zod schema in `server/src/alerts/types.ts` and the
 * routes in `server/src/routes/alerts/`.
 *
 * Adding a new alert type or filter shape: update both here and on
 * the server (the Zod schema is the API source of truth).
 */

export type AlertType =
  | 'overtime'
  | 'shipedge_report_sync_failure'
  | 'training_overdue'
  | 'cs_sla_breach'
  | 'cs_reassignment_burst'
  | 'cs_integration_failure'
  | 'cs_credential_rotation_due'
  | 'integration_sync_failure';

interface AlertTypeOption {
  value: AlertType;
  label: string;
  description: string;
}

// Map form lets us look up by `alertType` value without a `find` —
// TS doesn't flag tautologies on object-key access. Adding a second
// alert type is a single new entry below.
export const ALERT_TYPE_BY_VALUE: Record<AlertType, AlertTypeOption> = {
  overtime: {
    value: 'overtime',
    label: 'Overtime',
    description: 'Daily trim recommendations + 40-hour cap breach alerts for hourly employees.',
  },
  shipedge_report_sync_failure: {
    value: 'shipedge_report_sync_failure',
    label: 'ShipEdge sync failure',
    description:
      'Fires when a nightly or manual ShipEdge report pull fails — recipients can resync from the Sync Log page.',
  },
  training_overdue: {
    value: 'training_overdue',
    label: 'Training overdue',
    description:
      'Fires when an assigned course passes its due date without completion — one alert per overdue window.',
  },
  cs_sla_breach: {
    value: 'cs_sla_breach',
    label: 'CS SLA breach',
    description:
      'Fires when a customer ticket passes the first-reply SLA escalation threshold without an agent response.',
  },
  cs_reassignment_burst: {
    value: 'cs_reassignment_burst',
    label: 'CS reassignment burst',
    description:
      'Fires when one person manually reassigns an unusual burst of tickets in a short window (mass-grab detection).',
  },
  cs_integration_failure: {
    value: 'cs_integration_failure',
    label: 'CS integration failure',
    description:
      'Fires when the Chatwoot connection is failing — webhook errors, pull-sync failures, or a failed health check.',
  },
  cs_credential_rotation_due: {
    value: 'cs_credential_rotation_due',
    label: 'CS credential rotation due',
    description:
      'Fires when a tracked CS credential passes its rotation due date — rotate from the Operations tab.',
  },
  integration_sync_failure: {
    value: 'integration_sync_failure',
    label: 'Integration sync failure',
    description:
      "Fires when a connected integration's sync hard-fails — bad credentials, an unreachable host, or a config problem. Reconnect from Settings → Integrations.",
  },
};

export const ALERT_TYPE_OPTIONS: AlertTypeOption[] = Object.values(ALERT_TYPE_BY_VALUE);

/**
 * Render-safe lookup. The server can (now or in future versions) return
 * alert types this client build doesn't know about — an unknown type
 * must degrade to a readable label, never crash the render. This is
 * the regression guard for the "white screen on unknown alertType" bug.
 */
export function alertTypeOption(value: string): AlertTypeOption {
  const known = (ALERT_TYPE_BY_VALUE as Record<string, AlertTypeOption | undefined>)[value];
  return (
    known ?? {
      value: value as AlertType,
      label: value.replace(/_/g, ' '),
      description: 'Unknown alert type (newer than this app build).',
    }
  );
}

export type AlertChannel = 'email';

export type SubscriptionFilter = { kind: 'all' } | { kind: 'employee_in'; employeeMembershipIds: string[] };

export interface AlertSubscription {
  id: string;
  alertType: AlertType;
  filterJson: SubscriptionFilter;
  recipientMembershipId: string;
  channel: AlertChannel;
  createdByMembershipId: string | null;
  isActive: boolean;
  createdAt: string;
}

export async function listSubscriptions(includeInactive = false): Promise<AlertSubscription[]> {
  const query = includeInactive ? '?includeInactive=true' : '';
  const res = await apiClient.get<{ data: AlertSubscription[] }>(`/api/alerts/subscriptions${query}`);
  return res.data.data;
}

export interface CreateSubscriptionRequest {
  alertType: AlertType;
  recipientMembershipId: string;
  channel?: AlertChannel;
  filter: SubscriptionFilter;
}

export async function createSubscription(body: CreateSubscriptionRequest): Promise<AlertSubscription> {
  const res = await apiClient.post<{ data: AlertSubscription }>('/api/alerts/subscriptions', body);
  return res.data.data;
}

export async function deleteSubscription(id: string): Promise<void> {
  await apiClient.delete(`/api/alerts/subscriptions/${encodeURIComponent(id)}`);
}

// ── Events + dispatches (the Dispatches tab) ────────────────────────────

export type AlertDispatchStatus = 'pending' | 'sent' | 'failed';

export interface AlertDispatchRow {
  id: string;
  recipientName: string;
  recipientEmail: string;
  status: AlertDispatchStatus;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

export interface AlertEventRow {
  id: string;
  alertType: AlertType;
  severity: 'info' | 'trim' | 'breach';
  subjectMembershipId: string | null;
  payload: { subject: string; bodyText: string; details?: Record<string, unknown> };
  occurredAt: string;
  dispatches: AlertDispatchRow[];
}

export interface ListAlertEventsQuery {
  since?: string;
  type?: AlertType;
  limit?: number;
}

export async function listAlertEvents(q: ListAlertEventsQuery = {}): Promise<AlertEventRow[]> {
  const params = new URLSearchParams();
  if (q.since) params.set('since', q.since);
  if (q.type) params.set('type', q.type);
  if (q.limit !== undefined) params.set('limit', String(q.limit));
  const qs = params.toString();
  const res = await apiClient.get<{ data: AlertEventRow[] }>(`/api/alerts/events${qs ? `?${qs}` : ''}`);
  return res.data.data;
}
